import supabase from "../config/supabaseConfig.js"; // client central (mesmo padrão de professores.service)

// Regras de avaliação por tier (§1 validado). Moradia oficial das regras —
// CadastroAlunoNovo e EditarAluno importam DAQUI (antes duplicavam o preview).
// `q`/`w` são os textos exibidos na agenda lateral; o offset em meses é derivado
// de `w` por offsetMeses ("na entrada" = data_inicio; "após N meses" = +N meses).
export const REGRAS_AVAL = {
  "8 Check-in": [{ n: 1, q: "1ª avaliação", w: "na entrada" }],
  "12 Check-in": [
    { n: 1, q: "1ª avaliação", w: "na entrada" },
    { n: 2, q: "2ª avaliação", w: "após 4 meses" },
  ],
  "16 Check-in": [
    { n: 1, q: "1ª avaliação", w: "na entrada" },
    { n: 2, q: "2ª avaliação", w: "após 3 meses" },
    { n: 3, q: "3ª avaliação", w: "após 6 meses" },
  ],
  Premium: [
    { n: 1, q: "1ª avaliação", w: "na entrada" },
    { n: 2, q: "2ª avaliação", w: "após 3 meses" },
    { n: 3, q: "3ª avaliação", w: "após 6 meses" },
    { n: 4, q: "4ª avaliação", w: "após 9 meses" },
  ],
  Black: [
    { n: 1, q: "1ª avaliação", w: "na entrada" },
    { n: 2, q: "2ª avaliação", w: "após 3 meses" },
    { n: 3, q: "3ª avaliação", w: "após 6 meses" },
    { n: 4, q: "4ª avaliação", w: "após 9 meses" },
  ],
};

// Duração do form (Mensal/Semestral/Anual) ↔ formato do banco (mensal/...).
const MESES_POR_FORMATO = { mensal: 1, semestral: 6, anual: 12 };

// "na entrada" → 0; "após N meses" → N.
const offsetMeses = (w) => {
  if (/entrada/i.test(w)) return 0;
  const m = String(w).match(/(\d+)\s*mes/i);
  return m ? parseInt(m[1], 10) : 0;
};

// Soma `n` meses a uma data ISO (YYYY-MM-DD) usando UTC pra não sofrer com fuso.
const addMeses = (dataISO, n) => {
  const [y, mo, d] = dataISO.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1 + n, d)).toISOString().slice(0, 10);
};

// data_fim do contrato a partir do formato (mensal +1, semestral +6, anual +12).
const calcularDataFim = (dataInicio, formato) =>
  addMeses(dataInicio, MESES_POR_FORMATO[formato] || 1);

// Intervalo de recorrência do tier, em meses — derivado das REGRAS_AVAL: é o
// espaçamento entre as duas últimas avaliações da regra (16 Check-in: 3→6 = 3;
// 12 Check-in: 0→4 = 4; Premium/Black: 6→9 = 3). Tier com uma única avaliação
// ("8 Check-in" = só a inicial) NÃO tem recorrência e devolve null.
export const intervaloMeses = (tierLegado) => {
  const regras = REGRAS_AVAL[tierLegado] || [];
  if (regras.length < 2) return null;
  const offs = regras.map((r) => offsetMeses(r.w));
  return offs[offs.length - 1] - offs[offs.length - 2];
};

// Próxima avaliação a partir da ÚLTIMA REALIZADA (agenda rolante, 27/07).
// Devolve null quando não há o que agendar: sem data de última avaliação
// (aluno nunca avaliado), plano mensal ou tier sem recorrência.
export const calcularProximaAvaliacao = ({
  dataUltimaAvaliacao,
  formato,
  tierLegado,
}) => {
  if (!dataUltimaAvaliacao) return null;
  if (formato === "mensal") return null;
  const intervalo = intervaloMeses(tierLegado);
  if (!intervalo) return null;
  return addMeses(dataUltimaAvaliacao, intervalo);
};

// Gravação barrada pela RLS não dá erro: volta 0 linhas. Com .single() isso
// virava "JSON object requested, multiple (or no) rows returned" na tela
// (professor trocando plano pela Sala, 07/10). Aqui vira mensagem legível.
const umaLinha = (data, acao) => {
  if (!data || data.length === 0) {
    throw new Error(
      `Sem permissão para ${acao}. Peça para a recepção ou um admin.`
    );
  }
  return data[0];
};

// Resolve o plano_id (e demais dados do plano) a partir do label legado do front.
const resolverPlano = async (tierLegado) => {
  const { data, error } = await supabase
    .from("planos")
    .select("id, nome, tier_legado, avaliacoes_ano")
    .eq("tier_legado", tierLegado)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error(`Plano "${tierLegado}" não encontrado.`);
  return data;
};

export const matriculasService = {
  // Catálogo de planos ativos (ordem estável por tier legado).
  async getPlanos() {
    const { data, error } = await supabase
      .from("planos")
      .select("*")
      .eq("ativo", true)
      .order("id");
    if (error) throw error;
    return data;
  },

  // Matrícula ativa de um aluno (ou null), já com o plano embutido.
  async getMatriculaAtiva(alunoId) {
    const { data, error } = await supabase
      .from("matriculas")
      .select("*, planos(nome, tier_legado, avaliacoes_ano)")
      .eq("aluno_id", alunoId)
      .eq("status", "ativa")
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  // Cria a matrícula ativa. Retorna a linha do banco + tierLegado (conveniência
  // pra encadear direto em gerarAgendaAvaliacoes sem re-consultar o plano).
  async criarMatricula({ alunoId, tierLegado, formato, dataInicio }) {
    const plano = await resolverPlano(tierLegado);
    const dataFim = calcularDataFim(dataInicio, formato);
    const { data, error } = await supabase
      .from("matriculas")
      .insert([
        {
          aluno_id: alunoId,
          plano_id: plano.id,
          formato,
          data_inicio: dataInicio,
          data_fim: dataFim,
          status: "ativa",
        },
      ])
      .select();
    if (error) throw error;
    return { ...umaLinha(data, "criar a matrícula"), tierLegado };
  },

  // Atualiza plano/formato/data_inicio/data_fim da matrícula. `dataInicio` é a
  // data real do CONTRATO vinda do form (histórico — não mexe na agenda).
  // `dataUltimaAvaliacao` é a âncora da agenda rolante (27/07); quando vem
  // undefined a coluna não é tocada. updated_at manual (sem trigger).
  async atualizarMatricula(
    matriculaId,
    { tierLegado, formato, dataInicio, dataUltimaAvaliacao }
  ) {
    const plano = await resolverPlano(tierLegado);
    const dataFim = calcularDataFim(dataInicio, formato);
    const patch = {
      plano_id: plano.id,
      formato,
      data_inicio: dataInicio,
      data_fim: dataFim,
      updated_at: new Date().toISOString(),
    };
    if (dataUltimaAvaliacao !== undefined) {
      patch.data_ultima_avaliacao = dataUltimaAvaliacao || null;
    }
    const { data, error } = await supabase
      .from("matriculas")
      .update(patch)
      .eq("id", matriculaId)
      .select();
    if (error) throw error;
    return { ...umaLinha(data, "alterar a matrícula"), tierLegado };
  },

  // Dar baixa numa avaliação: carimba a data como a última realizada da
  // matrícula e já agenda a próxima. É o que fecha o ciclo do modelo rolante
  // (27/07) — sem isto, marcar "realizada" não produzia nada visível e a
  // agenda do aluno morria ali.
  async registrarAvaliacaoRealizada(matriculaId, dataRealizada) {
    const { data, error } = await supabase
      .from("matriculas")
      .update({
        data_ultima_avaliacao: dataRealizada,
        updated_at: new Date().toISOString(),
      })
      .eq("id", matriculaId)
      .select();
    if (error) throw error;
    const matricula = umaLinha(data, "registrar a avaliação");
    await this.agendarProximaAvaliacao(matricula);
    return matricula;
  },

  // Agenda a PRÓXIMA avaliação a partir da última realizada (modelo rolante,
  // 27/07 — substitui o gerarAgendaAvaliacoes automático):
  //  1. apaga as PENDENTES da matrícula (as realizadas NUNCA são tocadas) —
  //     no modelo rolante existe no máximo UMA pendente por vez, sempre
  //     derivada da última realizada;
  //  2. calcula `data_ultima_avaliacao + intervalo do tier`;
  //  3. insere UMA linha, MESMO se a data já passou — data no passado é
  //     precisamente o que significa "avaliação atrasada", e é quando o aluno
  //     mais precisa aparecer no painel. (Bug do 1º deploy de 27/07: eu
  //     filtrava `proxima < hoje` e o aluno atrasado sumia — pego no teste do
  //     Rodrigo com o Tales Coutinho, última avaliação 01/01, Black = +3 meses,
  //     próxima 01/04 já vencida, nada era agendado.)
  // Sem data de última avaliação (aluno nunca avaliado), plano mensal ou tier
  // sem recorrência: não agenda nada — o aluno fica como "nunca avaliado", que
  // é a lista que a academia precisa chamar.
  async agendarProximaAvaliacao(matricula) {
    const { error: delErro } = await supabase
      .from("avaliacoes")
      .delete()
      .eq("matricula_id", matricula.id)
      .is("data_realizada", null);
    if (delErro) throw delErro;

    let tierLegado = matricula.tierLegado;
    if (!tierLegado && matricula.plano_id) {
      const { data: plano, error } = await supabase
        .from("planos")
        .select("tier_legado")
        .eq("id", matricula.plano_id)
        .single();
      if (error) throw error;
      tierLegado = plano.tier_legado;
    }

    const proxima = calcularProximaAvaliacao({
      dataUltimaAvaliacao: matricula.data_ultima_avaliacao,
      formato: matricula.formato,
      tierLegado,
    });
    if (!proxima) return null;

    const { data, error } = await supabase
      .from("avaliacoes")
      .insert([
        {
          aluno_id: matricula.aluno_id,
          matricula_id: matricula.id,
          tipo: "reavaliacao",
          data_agendada: proxima,
        },
      ])
      .select();
    if (error) throw error;
    return umaLinha(data, "agendar a próxima avaliação");
  },

  // REMOVIDO em 27/07: `gerarAgendaAvaliacoes` criava a agenda inteira a partir
  // de `data_inicio` a cada troca de tier/duração. Para os alunos vindos da
  // migração (data_inicio de 2025) isso dava agenda vazia; e enquanto o form
  // sobrescrevia data_inicio com a data da edição, a 1ª avaliação nascia no dia
  // da edição — já atrasada. Foram as 39 avaliações fantasma apagadas em 27/07.
  // Substituído por `agendarProximaAvaliacao` (agenda rolante, acima).
};

export default matriculasService;
